import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { staggeredBaseQuery } from "./staggeredBaseQuery";
import {
  MarinerProfile,
  marinerSearchParams,
} from "../../common/MarinerProfile";
import { PaginatedResponse } from "../../common/PaginatedResponse";
import { jobRatingStringToEnum } from "../../util/jobRating.util";
import { User } from "../../common/User";

export const marinerApi = createApi({
  reducerPath: "marinerApi",
  tagTypes: ["Mariner"],
  baseQuery: staggeredBaseQuery(""),
  endpoints: (builder) => ({
    getMariners: builder.query<
      PaginatedResponse<MarinerProfile>,
      Partial<marinerSearchParams>
    >({
      query: ({
        search,
        ratings,
        vesselId,
        registered,
        compatibleWithRating,
        smis,
        veteran,
        trainingStatus,
        page,
        size,
        sortColumn,
        sortDirection,
      }) => {
        const enumRatings = ratings?.map((item) => jobRatingStringToEnum(item));
        let url = `profiles/${"&search=" + search}${
          !!ratings && ratings?.length > 0 ? "&ratings=" + enumRatings : ""
        }${!!vesselId ? "&vesselId=" + vesselId : ""}${
          registered !== undefined ? "&registered=" + registered : ""
        }${
          veteran !== undefined ? "&veteran=" + veteran : ""
        }${
          trainingStatus !== "" ? "&trainingStatus=" + trainingStatus : ""
        }${
          !!compatibleWithRating && compatibleWithRating !== ""
            ? "&compatibleWithRating=" +
              jobRatingStringToEnum(compatibleWithRating)
            : ""
        }${!!smis && smis !== "" ? `&smis=${smis.replaceAll('#', '%23')}` : ""}${"&page=" + page}${
          "&size=" + size
        }${
          !!sortColumn && sortColumn !== "" ? "&sortColumn=" + sortColumn : ""
        }${
          !!sortColumn && sortColumn !== ""
            ? "&sortDirection=" + sortDirection?.toUpperCase()
            : ""
        }
        `;
        url = url.replace("profiles/&", "profiles?");
        return {
          method: "GET",
          url,
        };
      },
      providesTags: (result) =>
        result && !!result.content
          ? [
              ...result.content.map(({ id }) => ({
                type: "Mariner" as const,
                id,
              })),
              { type: "Mariner", id: "LIST" },
            ]
          : [{ type: "Mariner", id: "LIST" }],
    }),
    getMariner: builder.query<MarinerProfile, String>({
      query: (profileId) => {
        return {
          method: "GET",
          url: `profiles/${profileId}`,
        };
      },
    }),
    createMariner: builder.mutation<MarinerProfile, Partial<MarinerProfile>>({
      query: (profile) => {
        return {
          method: "POST",
          url: "profiles/",
          body: profile,
        };
      },
      invalidatesTags: [{ type: "Mariner", id: "LIST" }],
    }),
    updateMariner: builder.mutation<
      MarinerProfile,
      { profileId: string; profile: Partial<MarinerProfile> }
    >({
      query: ({ profileId, profile }) => {
        return {
          method: "PUT",
          url: `profiles/${profileId}`,
          body: profile,
        };
      },
      invalidatesTags: (result, error, { profileId }) => [
        { type: "Mariner", id: profileId },
      ],
    }),
    adminCreateProfileUser: builder.mutation<User, String>({
      query: (profileId) => {
        return {
          method: "POST",
          url: `profiles/${profileId}/register`,
        };
      },
    }),
    adminResendRegistrationEmail: builder.mutation<void, String>({
      query: (profileId) => {
        return {
          method: "POST",
          url: `profiles/${profileId}/resend`,
        };
      },
    }),
    getPermanentOfficers: builder.query<MarinerProfile[], string>({
      query: (vesselId) => {
        return {
          method: "GET",
          url: `profiles/vessel/${vesselId}`,
        };
      },
    }),
  }),
});

export const {
  useGetMarinersQuery,
  useGetMarinerQuery,
  useCreateMarinerMutation,
  useUpdateMarinerMutation,
  useAdminCreateProfileUserMutation,
  useAdminResendRegistrationEmailMutation,
  useGetPermanentOfficersQuery,
} = marinerApi;
